import React, { useMemo } from 'react';
import {
  countEventsByCalendarDay,
  calendarDayKey,
  getEventCalendarDay,
  isSameCalendarDay,
} from '../../services/dateUtils';
import friendsIcon from '../../assets/icons/friends.png';
import './PublicCalendar.css';

const PUBLIC_MONTH_COUNT = 2;
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const FRIENDS_AVATAR_SLOTS = 2;

function monthStartFromOffset(offset, referenceDate) {
  const ref = referenceDate ?? new Date();
  return new Date(ref.getFullYear(), ref.getMonth() + offset, 1);
}

function buildMonthGrid(monthStart) {
  const year = monthStart.getFullYear();
  const month = monthStart.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDow = new Date(year, month, 1).getDay();
  const mondayOffset = (firstDow + 6) % 7;

  const cells = [];
  for (let i = 0; i < mondayOffset; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) {
    cells.push(new Date(year, month, d));
  }
  return cells;
}

function formatMonthLabel(monthStart) {
  return monthStart.toLocaleDateString('en-GB', { month: 'long' });
}

function formatMonthMeta(monthStart, eventCount) {
  const noun = eventCount === 1 ? 'event' : 'events';
  return `${formatMonthLabel(monthStart)} · ${eventCount} ${noun}`;
}

function countMonthEvents(cells, dayCounts) {
  let monthEventCount = 0;
  cells.forEach((day) => {
    if (day) monthEventCount += dayCounts[calendarDayKey(day)] || 0;
  });
  return monthEventCount;
}

/**
 * Prefer the reference month when it has friend events; otherwise the month of
 * the soonest upcoming friend event (else latest past).
 */
function friendsMonthStartForEvents(friendsEvents, referenceDate) {
  const ref = referenceDate ?? new Date();
  const currentStart = monthStartFromOffset(0, ref);
  const dayCounts = countEventsByCalendarDay(friendsEvents);
  const currentCount = countMonthEvents(buildMonthGrid(currentStart), dayCounts);
  if (currentCount > 0) return currentStart;

  const days = [];
  (friendsEvents || []).forEach((event) => {
    const day = getEventCalendarDay(event);
    if (day) days.push(day);
  });
  if (days.length === 0) return currentStart;

  const refDay = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
  const upcoming = days
    .filter((day) => day.getTime() >= refDay.getTime())
    .sort((a, b) => a.getTime() - b.getTime());
  const pick = upcoming[0]
    || days.slice().sort((a, b) => b.getTime() - a.getTime())[0];
  return new Date(pick.getFullYear(), pick.getMonth(), 1);
}

/** Discrete badge size tier from event count (O(1)). */
function eventCountBadgeTier(count) {
  if (count >= 6) return 'xl';
  if (count >= 4) return 'lg';
  if (count >= 2) return 'md';
  return 'sm';
}

/** Unique friends interested in events on a given calendar day. */
function friendsForDay(friendsEvents, day) {
  const byId = new Map();
  (friendsEvents || []).forEach((event) => {
    const eventDay = getEventCalendarDay(event);
    if (!eventDay || !isSameCalendarDay(eventDay, day)) return;
    (event.friendsGoing || []).forEach((friend) => {
      if (!friend?.id || byId.has(friend.id)) return;
      byId.set(friend.id, friend);
    });
  });
  return Array.from(byId.values());
}

function FriendsDayStack({ friends }) {
  if (!friends.length) return null;

  const withPhoto = friends.filter((friend) => friend.photoURL);
  const shown = withPhoto.slice(0, FRIENDS_AVATAR_SLOTS);
  const overflow = friends.length - shown.length;

  return (
    <span className="pub-cal-friends-stack" aria-hidden="true">
      {shown.length === 0 ? (
        <img src={friendsIcon} alt="" className="pub-cal-friends-stack__icon" draggable={false} />
      ) : (
        shown.map((friend) => (
          <img
            key={friend.id}
            src={friend.photoURL}
            alt=""
            className="pub-cal-friends-stack__avatar"
            draggable={false}
          />
        ))
      )}
      {overflow > 0 && (
        <span className="pub-cal-friends-stack__more pub-mono">+{overflow}</span>
      )}
    </span>
  );
}

/**
 * Personal panel + friends panel + current and next public months. Guests see
 * personal and friends panels blurred/locked until login.
 * Panel order is fixed: Friends → Yours → current public month → next public month.
 * `panels="personalOnly"` — single “Liked Events” month (public profile teaser).
 * `panels="publicOnly"` — current and next public months (admin Event Database).
 * `panels="friendsAndPublic"` — friends panel + public months (events homepage).
 *
 * @param {{
 *   events: Array<Record<string, unknown>>,
 *   publicDayCounts?: Record<string, number>,
 *   yourEvents?: Array<Record<string, unknown>>,
 *   friendsEvents?: Array<Record<string, unknown>>,
 *   selectedDate: Date|null,
 *   selectedDates?: Date[],
 *   calendarFilterSource?: 'public' | 'personal' | 'friends',
 *   onSelectDate: (date: Date|null, source?: 'public' | 'personal' | 'friends') => void,
 *   onSelectDates?: (dates: Date[], source?: 'public' | 'personal' | 'friends') => void,
 *   referenceDate?: Date,
 *   personalLocked?: boolean,
 *   loading?: boolean,
 *   panels?: 'default' | 'personalOnly' | 'publicOnly' | 'friendsAndPublic',
 *   personalTitle?: string,
 *   lockedOverlayLabel?: string,
 *   onUnlockPersonal?: () => void,
 *   onViewPersonal?: () => void,
 *   onViewFriends?: () => void,
 *   onSharePersonal?: () => void,
 *   sharePersonalBusy?: boolean,
 *   sharePersonalLabel?: string,
 * }} props
 */
const PublicCalendar = ({
  events,
  publicDayCounts: publicDayCountsProp,
  yourEvents = [],
  friendsEvents = [],
  selectedDate,
  selectedDates,
  calendarFilterSource = 'public',
  onSelectDate,
  onSelectDates,
  referenceDate,
  personalLocked = false,
  loading = false,
  panels = 'default',
  personalTitle = 'Your Liked Events',
  lockedOverlayLabel = 'Locked until you log in',
  onUnlockPersonal,
  onViewPersonal,
  onViewFriends,
  onSharePersonal,
  sharePersonalBusy = false,
  sharePersonalLabel = 'Share',
}) => {
  const personalOnly = panels === 'personalOnly';
  const publicOnly = panels === 'publicOnly';
  const friendsAndPublic = panels === 'friendsAndPublic';
  const countedPublicDayCounts = useMemo(() => countEventsByCalendarDay(events), [events]);
  const publicDayCounts = publicDayCountsProp || countedPublicDayCounts;
  const personalDayCounts = useMemo(
    () => countEventsByCalendarDay(yourEvents),
    [yourEvents]
  );
  const friendsDayCounts = useMemo(
    () => countEventsByCalendarDay(friendsEvents),
    [friendsEvents]
  );

  const months = useMemo(() => {
    const ref = referenceDate ?? new Date();
    const personalMonthStart = monthStartFromOffset(0, ref);
    const personalCells = buildMonthGrid(personalMonthStart);
    const personalEventCount = countMonthEvents(personalCells, personalDayCounts);

    const personal = {
      monthStart: personalMonthStart,
      cells: personalCells,
      personal: true,
      friends: false,
      dayCounts: personalDayCounts,
      meta: formatMonthMeta(personalMonthStart, personalEventCount),
    };

    if (personalOnly) {
      return [personal];
    }

    const publicMonths = Array.from({ length: PUBLIC_MONTH_COUNT }, (_, offset) => {
      const monthStart = monthStartFromOffset(offset, ref);
      const cells = buildMonthGrid(monthStart);
      const monthEventCount = countMonthEvents(cells, publicDayCounts);
      return {
        monthStart,
        cells,
        personal: false,
        friends: false,
        dayCounts: publicDayCounts,
        meta: formatMonthMeta(monthStart, monthEventCount),
      };
    });

    if (publicOnly) {
      return publicMonths;
    }

    const friendsMonthStart = friendsMonthStartForEvents(friendsEvents, ref);
    const friendsCells = buildMonthGrid(friendsMonthStart);
    const friendsEventCount = countMonthEvents(friendsCells, friendsDayCounts);
    const friends = {
      monthStart: friendsMonthStart,
      cells: friendsCells,
      personal: false,
      friends: true,
      dayCounts: friendsDayCounts,
      meta: formatMonthMeta(friendsMonthStart, friendsEventCount),
    };

    if (friendsAndPublic) {
      return [friends, ...publicMonths];
    }

    return [friends, personal, ...publicMonths];
  }, [
    personalDayCounts,
    friendsDayCounts,
    publicDayCounts,
    friendsEvents,
    referenceDate,
    personalOnly,
    publicOnly,
    friendsAndPublic,
  ]);

  const multi = Array.isArray(selectedDates);
  const activeDates = multi
    ? selectedDates.filter((day) => day instanceof Date && !Number.isNaN(day.getTime()))
    : (selectedDate ? [selectedDate] : []);

  const handleDayClick = (day, count, source) => {
    if (loading || !count) return;
    if (multi) {
      if (calendarFilterSource !== source) {
        onSelectDates?.([day], source);
        return;
      }
      const exists = activeDates.some((selected) => isSameCalendarDay(selected, day));
      const next = exists
        ? activeDates.filter((selected) => !isSameCalendarDay(selected, day))
        : [...activeDates, day];
      onSelectDates?.(next, source);
      return;
    }
    if (
      selectedDate
      && isSameCalendarDay(selectedDate, day)
      && calendarFilterSource === source
    ) {
      onSelectDate(null);
    } else {
      onSelectDate(day, source);
    }
  };

  return (
    <div className={`pub-cal-wrap${loading ? ' pub-cal-wrap--loading' : ''}`}>
      <div
        className="pub-cal"
        data-testid="public-calendar"
        aria-busy={loading ? 'true' : undefined}
      >
        {months.map(({ monthStart, cells, meta, personal, friends, dayCounts }) => {
          const locked = (personal || friends) && personalLocked;
          const source = personal ? 'personal' : friends ? 'friends' : 'public';
          const panelKey = personal ? 'yours' : friends ? 'friends' : 'pub';

          return (
            <div
              key={`${calendarDayKey(monthStart)}-${panelKey}`}
              className={[
                'pub-cal-month',
                personal && 'pub-cal-month--yours',
                friends && 'pub-cal-month--friends',
                locked && 'pub-cal-month--locked',
                loading && 'pub-cal-month--loading',
              ].filter(Boolean).join(' ')}
              data-testid={
                personal
                  ? 'pub-cal-month-yours'
                  : friends
                    ? 'pub-cal-month-friends'
                    : `pub-cal-month-${monthStart.getMonth()}`
              }
            >
              {personal ? (
                <div className="pub-cal-yours-heading">
                  <h3 className="pub-cal-month-header pub-cal-month-header--yours">
                    {personalTitle}
                  </h3>
                  <p className="pub-cal-month-meta">{meta}</p>
                </div>
              ) : friends ? (
                <div className="pub-cal-friends-heading">
                  <h3 className="pub-cal-month-header pub-cal-month-header--friends">
                    Friends Liked
                  </h3>
                  <p className="pub-cal-month-meta pub-cal-month-meta--friends">{meta}</p>
                </div>
              ) : (
                <h3 className="pub-cal-month-header">{meta}</h3>
              )}
              <div className="pub-cal-month-main">
                <div className={locked ? 'pub-cal-month-blur' : undefined}>
                  <div className="pub-cal-body">
                    <div className="pub-cal-weekdays" aria-hidden="true">
                      {WEEKDAYS.map((d) => (
                        <span key={d} className="pub-cal-weekday pub-mono">{d}</span>
                      ))}
                    </div>
                    <div className="pub-cal-grid">
                      {cells.map((day, idx) => {
                        if (!day) {
                          return (
                            <div
                              key={`pad-${panelKey}-${monthStart.getMonth()}-${idx}`}
                              className="pub-cal-day pub-cal-day--pad"
                            />
                          );
                        }

                        const key = calendarDayKey(day);
                        const count = dayCounts[key] || 0;
                        const isActive = activeDates.some((selected) => isSameCalendarDay(selected, day))
                          && calendarFilterSource === source;
                        const isEmpty = count === 0;
                        const weekday = day.toLocaleDateString('en-GB', { weekday: 'short' });
                        const dayNum = String(day.getDate()).padStart(2, '0');
                        const dayDisabled = isEmpty || locked || loading;
                        const dayFriends = friends && count > 0
                          ? friendsForDay(friendsEvents, day)
                          : [];

                        return (
                          <button
                            key={`${panelKey}-${key}`}
                            type="button"
                            className={[
                              'pub-cal-day',
                              isEmpty && 'pub-cal-day--empty',
                              isActive && 'pub-cal-day--active',
                              !isEmpty && 'pub-cal-day--has-events',
                              friends && !isEmpty && 'pub-cal-day--friends',
                            ].filter(Boolean).join(' ')}
                            disabled={dayDisabled}
                            onClick={() => handleDayClick(day, count, source)}
                            aria-label={
                              friends
                                ? `${weekday} ${dayNum}, ${dayFriends.length} friend${dayFriends.length === 1 ? '' : 's'}, ${count} events`
                                : `${weekday} ${dayNum}, ${count} events`
                            }
                            aria-pressed={isActive || undefined}
                            data-testid={
                              personal
                                ? `pub-cal-yours-day-${key}`
                                : friends
                                  ? `pub-cal-friends-day-${key}`
                                  : `pub-cal-day-${key}`
                            }
                          >
                            <span className="pub-cal-day-num">{dayNum}</span>
                            {friends && count > 0 ? (
                              <FriendsDayStack friends={dayFriends} />
                            ) : (
                              count > 0 && (
                                <span
                                  className={`pub-cal-day-count pub-cal-day-count--${eventCountBadgeTier(count)} pub-mono`}
                                >
                                  {count}
                                </span>
                              )
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {personal && !locked && (onViewPersonal || onSharePersonal) && (
                    <div className="pub-cal-personal-actions">
                      {onViewPersonal && (
                        <button
                          type="button"
                          className="pub-cal-personal-btn"
                          onClick={onViewPersonal}
                        >
                          View
                        </button>
                      )}
                      {onSharePersonal && (
                        <button
                          type="button"
                          className="pub-cal-personal-btn pub-cal-personal-btn--ghost"
                          onClick={onSharePersonal}
                          disabled={sharePersonalBusy}
                        >
                          {sharePersonalBusy ? 'Sharing…' : sharePersonalLabel}
                        </button>
                      )}
                    </div>
                  )}
                  {friends && !locked && onViewFriends && (
                    <div className="pub-cal-personal-actions pub-cal-friends-actions">
                      <button
                        type="button"
                        className="pub-cal-personal-btn pub-cal-friends-btn"
                        onClick={onViewFriends}
                      >
                        View
                      </button>
                    </div>
                  )}
                </div>
                {locked && (
                  <button
                    type="button"
                    className="pub-cal-locked-overlay"
                    onClick={onUnlockPersonal}
                    data-testid={
                      friends ? 'pub-cal-friends-locked-overlay' : 'pub-cal-locked-overlay'
                    }
                  >
                    {lockedOverlayLabel}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {!personalOnly && !publicOnly ? (
        <p className="pub-cal-scroll-hint pub-mono" aria-hidden="true">Swipe →</p>
      ) : null}
    </div>
  );
};

export default PublicCalendar;
